import React from "react";
import { Modal, message } from "antd";

export default function ModalForm({
  formContext,
  width,
  children,
  title,
  isOpen,
  setOpen,
  handleSubmit,
  submitIsDisabled,
  changeData,
  setChangeData,
  tabsRef,
  onAfterSave,
  closeOnSuccess = true,
  createdRecord,
}) {
  const [messageApi, contextHolder] = message.useMessage();
  const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);

  const closeAndReset = () => {
    setOpen(false);
    if (setChangeData) {
      setChangeData(false);
    }
    if (formContext) {
      formContext.resetFields();
    }
  };
  
  const handleOk = () => {
    // Валидируем обязательные поля
    formContext
      .validateFields()
      .then(async (values) => {
        // После валидации получаем ВСЕ значения формы (включая из неактивных вкладок)
        // Это гарантирует, что мы получим все поля из всех вкладок
        const allValues = formContext.getFieldsValue(true);
        
        // Объединяем: сначала все значения из формы, затем валидированные (для перезаписи)
        // Это гарантирует, что мы получим все поля из всех вкладок
        const cleanedValues = { ...allValues, ...values };

        // Приводим комиссию к кратности 4 и диапазону 4-100
        const clampPercent = (val) => {
          if (val === null || val === undefined) return undefined;
          const num = Number(val);
          if (Number.isNaN(num)) return undefined;
          const rounded = Math.round(num / 4) * 4;
          return Math.min(100, Math.max(4, rounded));
        };
        const percent = clampPercent(cleanedValues.chatting_percent);

        if (percent === undefined) {
          delete cleanedValues.chatting_percent;
        } else {
          cleanedValues.chatting_percent = percent;
        }

        try {
          const result = await handleSubmit([cleanedValues]);

          // Если передан колбэк, вызываем его после успешного сохранения
          if (typeof onAfterSave === "function") {
            try {
              await onAfterSave(result);
            } catch (afterErr) {
              console.error("ModalForm onAfterSave error:", afterErr);
            }
          }

          if (closeOnSuccess) {
            setOpen(false);
            if (setChangeData) {
              setChangeData(false);
            }
            if (formContext) {
              formContext.resetFields();
            }
          }
        } catch (error) {
          // Если ошибка - это валидация на фронтенде (ValidationError или Error без response)
          // Показываем сообщение напрямую
          if (error?.name === "ValidationError" || (error?.message && !error?.response)) {
            messageApi.error({
              content: error.message,
              duration: 5,
            });
            return; // Не продолжаем обработку, так как уже показали сообщение
          }
          
          // Если ошибка от сервера (400 Bad Request или другие статусы), проверяем детали
          if (error?.response) {
            const errorData = error?.response?.data;
            let errorMessage = "Ошибка при сохранении данных";
            
            // Приоритет: detail > message > общее сообщение
            if (errorData?.detail) {
              if (typeof errorData.detail === "string") {
                errorMessage = errorData.detail;
              } else if (Array.isArray(errorData.detail)) {
                errorMessage = errorData.detail.map(err => 
                  typeof err === "string" ? err : err?.msg || JSON.stringify(err)
                ).join("; ");
              } else if (typeof errorData.detail === "object") {
                errorMessage = Object.keys(errorData.detail)
                  .map(field => `${field}: ${Array.isArray(errorData.detail[field]) 
                    ? errorData.detail[field].join(", ") 
                    : errorData.detail[field]}`)
                  .join("; ");
              }
            } else if (errorData?.message) {
              errorMessage = errorData.message;
            } else if (error?.message) {
              errorMessage = error.message;
            }
            
            messageApi.error({
              content: errorMessage,
              duration: 5,
            });
            return;
          }
          
          // Если ошибка без response, но с message (например, ValidationError)
          if (error?.message) {
            messageApi.error({
              content: error.message,
              duration: 5,
            });
            return;
          }

          const originalError = error?.originalError || error;
          const response = originalError?.response || error?.response;

          let errorMessage = "Произошла ошибка при сохранении данных";

          if (response?.status === 422) {
            const errorData = response?.data;
            if (errorData?.detail) {
              if (Array.isArray(errorData.detail)) {
                const errors = errorData.detail
                  .map((err) => {
                    if (typeof err === "string") return err;
                    if (err?.msg) {
                      const field = err.loc?.slice(1).join(".") || "поле";
                      return `${field}: ${err.msg}`;
                    }
                    return JSON.stringify(err);
                  })
                  .join("; ");
                errorMessage = errors;
              } else if (typeof errorData.detail === "string") {
                errorMessage = errorData.detail;
              } else if (typeof errorData.detail === "object") {
                const errorFields = Object.keys(errorData.detail)
                  .map((field) => {
                    const fieldErrors = Array.isArray(errorData.detail[field])
                      ? errorData.detail[field].join(", ")
                      : errorData.detail[field];
                    return `${field}: ${fieldErrors}`;
                  })
                  .join("; ");
                errorMessage = errorFields || JSON.stringify(errorData.detail);
              } else {
                errorMessage = JSON.stringify(errorData.detail);
              }
            } else if (errorData?.message) {
              errorMessage = errorData.message;
            }
          } else if (response?.data) {
            errorMessage =
              response.data?.message ||
              response.data?.detail ||
              error?.message ||
              errorMessage;
          } else if (error?.message) {
            errorMessage = error.message;
          }

          messageApi.error({
            content: errorMessage,
            duration: 5,
          });
        }
      })
      .catch((info) => {
        console.info("Validation error:", info);

        if (info.errorFields && info.errorFields.length > 0) {
          const firstError = info.errorFields[0];
          const fieldName = firstError.name?.[0] || "поле";
          const errorMessage = firstError.errors?.[0] || "Ошибка валидации";

          let tabHint = "";
          let tabKeyToSwitch = null;

          if (
            ["name", "type", "tags", "description_short", "description_long", "code", "unit", "category", "manufacturer", "cashback_type", "cashback_value"].includes(fieldName)
          ) {
            tabHint = " Перейдите на вкладку 'Основное'.";
            tabKeyToSwitch = "main";
          } else if (
            ["seo_title", "seo_description", "seo_keywords"].includes(fieldName)
          ) {
            tabHint = " Перейдите на вкладку 'SEO'.";
            tabKeyToSwitch = "seo";
          } else if (
            ["global_category_id", "chatting_percent", "qr_hash", "address", "latitude", "longitude"].includes(fieldName)
          ) {
            tabHint = " Перейдите на вкладку 'Быстро'.";
            tabKeyToSwitch = "quick";
          }

          messageApi.error(
            `Ошибка в поле "${fieldName}": ${errorMessage}${tabHint}`,
            5
          );

          if (tabKeyToSwitch && tabsRef?.current?.setActiveTab) {
            tabsRef.current.setActiveTab(tabKeyToSwitch);
            setTimeout(() => {
              const errorField = document.querySelector(
                `[name="${fieldName}"]`,
              );
              if (errorField) {
                errorField.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                errorField.focus();
              }
            }, 200);
          }
        } else {
          switch (info) {
            case "field required":
              messageApi.error("Вы не заполнили обязательные поля", 5);
              break;
            case "Цена с таким типом уже существует":
              messageApi.error("Цена с таким типом уже существует", 5);
              break;
            case "value is not a valid integer":
            case "value is not a valid float":
              messageApi.error("Значение не является допустимым числом", 5);
              break;
            default:
              messageApi.error(
                "Упс... Произошла необработанная ошибка. Попробуйте ещё раз",
                5
              );
              break;
          }
        }
      });
  };

  const handleCancel = () => {
    const hasUnsavedChanges =
      changeData === true ||
      (formContext &&
        typeof formContext.isFieldsTouched === "function" &&
        formContext.isFieldsTouched());

    if (hasUnsavedChanges) {
      setIsConfirmVisible(true);
      return;
    }

    closeAndReset();
  };

  const isAddPricesPhase = !!createdRecord;

  return (
    <>
      {contextHolder}
      <Modal
        open={isOpen}
        width={width || 800}
        title={title}
        onOk={() => {
          if (isAddPricesPhase) {
            closeAndReset();
          } else {
            handleOk();
          }
        }}
        okButtonProps={{
          disabled: submitIsDisabled,
        }}
        onCancel={() => handleCancel()}
        okText={isAddPricesPhase ? "Готово" : "Подтвердить"}
        cancelText={"Отмена"}
      >
        {children}
      </Modal>
      <Modal
        open={isConfirmVisible}
        title="Подтвердите действие"
        onOk={() => {
          setIsConfirmVisible(false);
          closeAndReset();
        }}
        onCancel={() => setIsConfirmVisible(false)}
        okText="Да"
        cancelText="Нет"
        zIndex={2000}
        getContainer={() => document.body}
      >
        <p>
          Вы действительно хотите закрыть окно? Несохранённые изменения будут потеряны.
        </p>
      </Modal>
    </>
  );
}
